import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import {
  getBuildingDesign,
  useBuildingDesign,
  useUpdateBuildingDesign,
} from './design.hooks';
import { mutateDesign } from '@shared/helpers/design/mutators';
import { isDesignSnapshotSupersededError } from '../helpers/design-snapshot-persist.helpers';
import {
  MezzanineStorey,
  NonMezzanineStorey,
  Storey,
} from '@shared/models/design/storey.model';
import { shouldIgnoreUndoRedoShortcut } from '@shared/helpers/event.helpers';
import {
  cancelEmptyBuildingCandidate,
  discardBuildingCandidate,
  getBuildingCandidate,
  getHasBuildingSelection,
  getSelectedBuildingId,
  useBuildingCandidate,
  useIsBuildingOutlineSelected,
  getSelectedStoreyId,
  useSelectedBuildingId,
  useSelectedStoreyId,
  useSetBuildingCandidate,
  useRequestQuickTopDown,
  useSetHoveredBuildingId,
  useSetSelectedBuildingId,
  useSetSelectedStoreyId,
  getWritableBuildingId,
} from '../store/design-store.hooks';
import {
  discardActiveDesignDraft,
  NO_BUILDING_SELECTED,
  suppressNextDesignMapLocationFlyTo,
  useDesignStore,
} from '../store/design.store';
import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { getBuilding } from '@shared/helpers/recursive-element.helpers';
import { getId } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  addEmptyBuildingToDesign,
  getBuildingById,
  getBuildingStoreys,
  getMezzanineParent,
  getMezzanineStoreysInStorey,
  getOtherBuildings,
  getBuildingStoreysWithSoleFallback,
  getStoreyBelow,
  getStoreyById,
  getStoreyDisplayName,
  getStoreyIndex,
  isEmptyBuildingGeometry,
} from '@shared/helpers/design/storey.helpers';
import {
  getDesignGfa,
  getGfaFromStoreys,
} from '@shared/helpers/design/shape.helpers';
import {
  canAddBuilding,
  removeEmptyBuildingOnNavigation,
  shouldSwitchStoreyOnBuildingSelect,
} from '@shared/helpers/design/building.helpers';
import { pruneUnreferencedDesignActivities } from '@shared/helpers/design/activities.helpers';
import { required } from '@shared/helpers/function.helpers';
import { ItemOrItemId, SemiPartial } from '@shared/models/type-helpers.model';
import { AMP_DESIGN_STOREY_ADD } from '@shared/constants';
import amplitudeLog from '@client/amplitude';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useSnackbarWithUndo } from '@client/hooks/use-undo-snackbar.hooks';
import { useProjectId } from '@client/store/project/project.hook';
import { shouldResetMissingSelectedStoreyId } from '../helpers/storey-selection.helpers';
import { getInitialEmptyBuildingCandidate } from '../helpers/building-candidate.helpers';

const selectedStoreySelector = (
  design: BuildingDesign,
  id: string | undefined,
  buildingId: string | undefined,
) => {
  if (!id) return undefined;
  const storeys = getBuildingStoreys(design, buildingId);
  return storeys.find((storey) => storey.id === id);
};

/**
 * Keep the design store pointed at a storey that still exists.
 *
 * Lives here rather than next to the other design-store selectors so that
 * module stays free of feature-hook imports (it would otherwise close a
 * `design.hooks → design-store.hooks → storeys.hooks` import cycle).
 */
export const useSyncDesignStore = (): void => {
  const storeys = useStoreys();
  const design = useBuildingDesign();
  const projectId = useProjectId();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const selectedStoreyId = useSelectedStoreyId();
  const selectedBuildingId = useSelectedBuildingId();
  const setSelectedBuildingId = useSetSelectedBuildingId();
  const buildingCandidate = useBuildingCandidate();
  const setBuildingCandidate = useSetBuildingCandidate();
  const requestQuickTopDown = useRequestQuickTopDown();
  const isBuildingOutlineSelected = useIsBuildingOutlineSelected();
  const projectIdRef = useRef(projectId);
  const previousStoreys = usePrevious(storeys);

  useEffect(() => {
    if (projectIdRef.current === projectId) return;
    projectIdRef.current = projectId;
    setSelectedBuildingId(undefined);
  }, [projectId, setSelectedBuildingId]);

  useEffect(() => {
    if (!selectedBuildingId) return;
    // A cleared selection is intentional and must survive design updates.
    if (selectedBuildingId === NO_BUILDING_SELECTED) return;
    const exists = design.buildings.some(
      (building) => building.id === selectedBuildingId,
    );
    if (!exists) setSelectedBuildingId(undefined);
  }, [design.buildings, selectedBuildingId, setSelectedBuildingId]);

  useEffect(() => {
    const candidate = getInitialEmptyBuildingCandidate(
      design,
      selectedBuildingId,
      buildingCandidate,
    );
    if (!candidate) return;
    setBuildingCandidate(candidate);
    setSelectedBuildingId(candidate.id);
    setSelectedStoreyId(candidate.storeys[0]?.id);
    requestQuickTopDown();
  }, [
    buildingCandidate,
    design,
    requestQuickTopDown,
    selectedBuildingId,
    setBuildingCandidate,
    setSelectedBuildingId,
    setSelectedStoreyId,
  ]);

  useEffect(() => {
    // A whole-building selection deliberately has no active storey.
    if (isBuildingOutlineSelected) return;
    const nextSelectedStoreyId = storeys[0]?.id;
    if (nextSelectedStoreyId === selectedStoreyId) return;
    // Snap a deleted storey to ground, but preserve an intentional no-selection
    // state and keep a newly added id until it lands on the list (DEV-3730).
    if (
      !shouldResetMissingSelectedStoreyId(
        storeys,
        selectedStoreyId,
        previousStoreys,
      )
    ) {
      return;
    }
    setSelectedStoreyId(nextSelectedStoreyId);
  }, [
    isBuildingOutlineSelected,
    previousStoreys,
    selectedStoreyId,
    setSelectedStoreyId,
    storeys,
  ]);
};

/**
 * Get the selected storey from the project geometry
 * @returns The selected storey or undefined if no storey is selected
 */
export const useSelectedStorey = (): Storey | undefined =>
  selectedStoreySelector(
    useBuildingDesign(),
    useSelectedStoreyId(),
    useSelectedBuildingId(),
  );

/**
 * Get the selected storey from the project geometry without causing a rerender
 * @returns The selected storey or undefined if no storey is selected
 */
export const getSelectedStorey = (): Storey | undefined =>
  selectedStoreySelector(
    getBuildingDesign(),
    getSelectedStoreyId(),
    getSelectedBuildingId(),
  );

/**
 * Hook to get the storeys of the selected building from the project geometry.
 * Falls back to the first building when no building is selected (DEV-2576).
 * @returns The storeys of the selected building
 */
export const useStoreys = (): Storey[] => {
  return getBuildingStoreys(useBuildingDesign(), useSelectedBuildingId());
};

/**
 * Storeys shown in the left panel. Unlike {@link useStoreys}, a cleared
 * selection still resolves to the only building of a single-building design,
 * so the panel shows real numbers instead of zeros (DEV-2576).
 * @returns The storeys the panel describes
 */
export const usePanelStoreys = (): Storey[] => {
  return getBuildingStoreysWithSoleFallback(
    useBuildingDesign(),
    useSelectedBuildingId(),
  );
};

/**
 * Hook to get every non-selected building, used to render them as static
 * footprints on the map (DEV-2576).
 * @returns Buildings except the selected one
 */
export const useOtherBuildings = (): Building[] => {
  return getOtherBuildings(useBuildingDesign(), useSelectedBuildingId());
};

/**
 * Select a {@link Building} so the storey list and map follow.
 * Keeps the current storey when the same building is already selected
 * (including unset id, which already means the first building).
 */
export const useSelectBuilding = (): ((buildingId: string) => void) => {
  const updateBuildingDesign = useUpdateBuildingDesign();
  const setSelectedBuildingId = useSetSelectedBuildingId();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const { enqueueSnackbar } = useSnackbar();
  return useCallback(
    (buildingId: string) => {
      const design = getBuildingDesign();
      const currentId = getSelectedBuildingId();
      if (
        !shouldSwitchStoreyOnBuildingSelect(
          buildingId,
          currentId,
          design.buildings[0]?.id,
        )
      ) {
        return;
      }

      const currentBuildingId = getHasBuildingSelection()
        ? (currentId ?? design.buildings[0]?.id)
        : undefined;
      const nextStoreys = getBuildingStoreys(design, buildingId);
      setSelectedBuildingId(buildingId);
      setSelectedStoreyId(nextStoreys[0]?.id);

      const candidateId = getBuildingCandidate()?.id;
      if (candidateId && candidateId !== buildingId) {
        discardBuildingCandidate(candidateId);
        // The candidate was the building being left, and it is not persisted.
        if (candidateId === currentBuildingId) return;
      }

      const currentBuilding = getBuildingById(design, currentBuildingId);
      if (
        !currentBuildingId ||
        !isEmptyBuildingGeometry(currentBuilding) ||
        design.buildings.length <= 1
      ) {
        return;
      }

      let didRemove = false;
      void updateBuildingDesign((latestDesign) => {
        const nextDesign = removeEmptyBuildingOnNavigation(
          latestDesign,
          currentBuildingId,
          buildingId,
        );
        didRemove = nextDesign !== latestDesign;
        return didRemove
          ? pruneUnreferencedDesignActivities(nextDesign)
          : latestDesign;
      })
        .then((updatedProject) => {
          if (!didRemove) return;
          const persisted = getBuilding(updatedProject).design;
          if (
            persisted.buildings.some(
              (building) => building.id === currentBuildingId,
            )
          ) {
            enqueueSnackbar('Could not remove the empty building', {
              variant: 'error',
            });
          }
        })
        .catch((error: unknown) => {
          if (isDesignSnapshotSupersededError(error)) return;
          enqueueSnackbar('Could not remove the empty building', {
            variant: 'error',
          });
        });
    },
    [
      updateBuildingDesign,
      setSelectedBuildingId,
      setSelectedStoreyId,
      enqueueSnackbar,
    ],
  );
};

/**
 * Clear the building selection so no building is highlighted (DEV-2576).
 * An unfinished new building is dropped too, so "+ New building" does not stay
 * disabled after a click in the left panel (DEV-3786).
 */
export const useDeselectBuilding = (): (() => void) => {
  const setSelectedBuildingId = useSetSelectedBuildingId();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const setHoveredBuildingId = useSetHoveredBuildingId();
  return useCallback(() => {
    const candidate = getBuildingCandidate();
    const selectingCandidate =
      candidate !== undefined && candidate.id === getSelectedBuildingId();
    if (candidate) {
      // Leaving draw mode must not keep the empty building around, or
      // "+ New building" stays disabled.
      cancelEmptyBuildingCandidate();
      discardActiveDesignDraft();
    }
    if (!selectingCandidate && getHasBuildingSelection()) {
      setSelectedBuildingId(NO_BUILDING_SELECTED);
      setSelectedStoreyId(undefined);
    }
    // A click does not necessarily move the pointer, so clear the map hover
    // immediately rather than waiting for a later mousemove event.
    setHoveredBuildingId(undefined);
  }, [setHoveredBuildingId, setSelectedBuildingId, setSelectedStoreyId]);
};

/**
 * Escape abandons an unfinished new building, including after a panel click
 * left it unselected with "+ New building" disabled (DEV-3786).
 */
export const useCancelEmptyBuildingOnEscape = (): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (shouldIgnoreUndoRedoShortcut(event)) return;
      // Blueprint measure and move own Escape until that tool is finished.
      if (useDesignStore.getState().blueprintInteractionMode !== 'locked') {
        return;
      }
      if (!getBuildingCandidate()) return;
      event.preventDefault();
      cancelEmptyBuildingCandidate();
      discardActiveDesignDraft();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
};

/**
 * Return the design selection to the state a brand new project starts in: no
 * explicit building choice (which resolves to the first building), no storey,
 * no hover and no candidate (DEV-2576).
 *
 * Used after removing the last building, where the design is re-seeded with a
 * single empty building. A cleared selection would instead leave the panel and
 * map pointing at a building that no longer exists.
 */
export const useResetBuildingSelection = (): (() => void) => {
  const setSelectedBuildingId = useSetSelectedBuildingId();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const setHoveredBuildingId = useSetHoveredBuildingId();
  const setBuildingCandidate = useSetBuildingCandidate();
  return useCallback(() => {
    setSelectedBuildingId(undefined);
    setSelectedStoreyId(undefined);
    setHoveredBuildingId(undefined);
    setBuildingCandidate(undefined);
  }, [
    setBuildingCandidate,
    setHoveredBuildingId,
    setSelectedBuildingId,
    setSelectedStoreyId,
  ]);
};

/**
 * Re-attach a cleared selection to the only building of the design (DEV-2576).
 *
 * Acting on a storey — adding one, clicking a row — is an implicit selection of
 * the building it belongs to. Without this the sentinel id keeps resolving to
 * no building, so the storey the user just acted on could not become active.
 * Designs with several buildings are left alone: there the panel shows the
 * overview, where no single building is implied.
 */
export const useAdoptSingleBuildingSelection = (): (() => void) => {
  const setSelectedBuildingId = useSetSelectedBuildingId();
  return useCallback(() => {
    if (getHasBuildingSelection()) return;
    if (getBuildingDesign().buildings.length !== 1) return;
    // `undefined` is the first building, which is the only one here.
    setSelectedBuildingId(undefined);
  }, [setSelectedBuildingId]);
};

/**
 * Create an ephemeral empty building candidate and select it. The candidate
 * is promoted by {@link useUpdateBuildingDesign} only after its first
 * footprint is completed.
 */
export const useAddEmptyBuilding = () => {
  const setBuildingCandidate = useSetBuildingCandidate();
  const setSelectedBuildingId = useSetSelectedBuildingId();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const requestQuickTopDown = useRequestQuickTopDown();
  return useCallback(async (): Promise<void> => {
    const design = getBuildingDesign();
    if (!canAddBuilding(design.buildings)) return;
    const sourceBuildingId = getHasBuildingSelection()
      ? getSelectedBuildingId()
      : undefined;
    const { building } = addEmptyBuildingToDesign(design, sourceBuildingId);
    setBuildingCandidate(building);
    setSelectedBuildingId(building.id);
    setSelectedStoreyId(building.storeys[0]?.id);
    requestQuickTopDown();
  }, [
    requestQuickTopDown,
    setBuildingCandidate,
    setSelectedBuildingId,
    setSelectedStoreyId,
  ]);
};

/**
 * Hook to remove a storey from the project geometry
 * @returns A function to remove a storey from the project geometry
 */
export const useRemoveStorey = () => {
  const updateBuildingDesign = useUpdateBuildingDesign();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const enqueueSnackbar = useSnackbarWithUndo();
  return useCallback(
    async (storeyOrId: Parameters<typeof mutateDesign.storey.remove>[1]) => {
      const buildingDesign = getBuildingDesign();
      const storey =
        guards.is.string(storeyOrId) || guards.is.number(storeyOrId)
          ? getStoreyById(buildingDesign, storeyOrId)
          : storeyOrId;

      if (!storey) return;

      const storeyId = getId(storeyOrId);
      const wasSelected = storeyId === getSelectedStoreyId();
      const index = wasSelected
        ? getStoreyIndex(buildingDesign, storeyOrId)
        : -1;

      let updatedDesign = buildingDesign;
      await updateBuildingDesign(
        (design) => {
          updatedDesign = mutateDesign.storey.remove(design, storeyOrId);
          return updatedDesign;
        },
        {
          onOptimisticLocalUpdate: () => {
            // Normalization may rebase the design origin after a storey is
            // removed. Remaining geometry stays put, so the camera must too.
            suppressNextDesignMapLocationFlyTo();
          },
        },
      );

      enqueueSnackbar('Storey deleted', { variant: 'success', withUndo: true });

      // If the removed storey was selected, select the storey below it (after update completes)
      if (wasSelected && index >= 0) {
        // Select the storey below (lower index) if it exists, otherwise select the one above
        const updatedStoreys = getBuildingStoreysWithSoleFallback(
          updatedDesign,
          getSelectedBuildingId(),
        );
        const storeyBelow =
          index > 0 ? updatedStoreys[index - 1] : updatedStoreys[0];
        if (storeyBelow) {
          setSelectedStoreyId(storeyBelow.id);
        } else {
          // No storeys left, clear selection
          setSelectedStoreyId(undefined);
        }
      }
    },
    [updateBuildingDesign, setSelectedStoreyId, enqueueSnackbar],
  );
};

/**
 * Add a new storey to the project geometry
 * @returns A function to add a new storey to the project geometry
 */
export const useAddStorey = () => {
  const updateBuildingDesign = useUpdateBuildingDesign();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const adoptSingleBuildingSelection = useAdoptSingleBuildingSelection();
  return useCallback(async (): Promise<void> => {
    const currentStoreyId = getSelectedStoreyId();
    adoptSingleBuildingSelection();
    let newStoreyId: string | undefined;
    let updatedProject;
    try {
      updatedProject = await updateBuildingDesign(
        (design) => {
          // A cleared selection must still write to a real building: the sentinel
          // id resolves to none, so fall back to the first one (DEV-2576).
          const buildingId = getWritableBuildingId();
          const result = mutateDesign.storey.add(design, buildingId);
          const resultStoreys = getBuildingStoreys(result, buildingId);
          newStoreyId = resultStoreys[resultStoreys.length - 1]?.id;
          return result;
        },
        {
          // Select after the new storey is in the project store so the missing-id
          // heal cannot snap back to the ground floor (DEV-3730).
          onOptimisticLocalUpdate: () => {
            if (newStoreyId) setSelectedStoreyId(newStoreyId);
          },
          // Runs after the update above, so it knows the new storey.
          events: (design) => {
            // No selection writes to the first building, as the add does.
            const buildingId =
              getWritableBuildingId() ?? design.buildings[0]?.id;
            if (!buildingId) return [];
            return [
              {
                kind: 'storey-added',
                buildingId,
                ...(newStoreyId ? { storeyId: newStoreyId } : {}),
              },
            ];
          },
        },
      );
    } catch (error: unknown) {
      if (isDesignSnapshotSupersededError(error)) return;
      throw error;
    }

    // Revert selection if the update fails
    if (guards.is.error(updatedProject)) {
      setSelectedStoreyId(currentStoreyId);
      return;
    }

    // Log storey add to amplitude
    amplitudeLog(AMP_DESIGN_STOREY_ADD);
  }, [adoptSingleBuildingSelection, updateBuildingDesign, setSelectedStoreyId]);
};

/**
 * Update a storey in the project geometry
 * @returns A function to update a storey in the project geometry
 */
export const useUpdateStoreys = () => {
  const updateBuildingDesign = useUpdateBuildingDesign();
  return useCallback(
    (...updates: SemiPartial<Storey, 'id'>[]) => {
      return updateBuildingDesign((design) =>
        mutateDesign.storey.update(design, ...updates),
      );
    },
    [updateBuildingDesign],
  );
};

export const useUpdateSelectedStorey = () => {
  const updateStorey = useUpdateStoreys();

  return useCallback(
    (update: Partial<Storey>) => {
      const selectedStoreyId = required(
        getSelectedStoreyId(),
        'No storey selected',
      );

      return updateStorey({ id: selectedStoreyId, ...update });
    },
    [updateStorey],
  );
};

/**
 * Check if a storey is the ground/bottom storey
 * @param storeyIdOrIndex
 * @returns true if the storey is the ground/bottom storey, false otherwise
 */
export const useIsGroundStorey = (
  storeyIdOrIndex: ItemOrItemId<Storey>,
): boolean => usePanelStoreys()[0]?.id === getId(storeyIdOrIndex);

export const useStoreyBelow = (
  storey: Storey,
): NonMezzanineStorey | undefined => {
  const storeys = usePanelStoreys();
  return getStoreyBelow(storeys, storey);
};

/**
 * Get the name of a storey or generate a fallback based on index if not defined
 */
export const useGetStoreyName: () => (storey: Storey) => string = () => {
  const storeys = usePanelStoreys();
  return useCallback(
    (storey: Storey) => getStoreyDisplayName(storey, storeys),
    [storeys],
  );
};

/**
 * Get the gross floor area (GFA) of a storey
 * @param storeyOrId
 * @returns The gross floor area (GFA) of the storey
 */
export const useStoreyGfa = (storeyOrId: ItemOrItemId<Storey>): number => {
  const gfaRecord = useStoreyGfaRecord();
  const storeyId = getId(storeyOrId);
  return useMemo(() => {
    return gfaRecord[storeyId] ?? 0;
  }, [gfaRecord, storeyId]);
};

/**
 * Get the gross floor area (GFA) of the selected building.
 * @returns The gross floor area (GFA) of the selected building
 */
export const useDesignGfa = (): number => {
  const storeys = usePanelStoreys();
  return useMemo(() => {
    return getGfaFromStoreys(storeys);
  }, [storeys]);
};

/**
 * Gross floor area of every building in the design (project-level GFA).
 * Use {@link useDesignGfa} for the selected building only.
 * @returns Sum of GFA across all buildings
 */
export const useProjectDesignGfa = (): number => {
  const design = useBuildingDesign();
  return useMemo(() => getDesignGfa(design), [design]);
};

export const useStoreyGfaRecord = (): Record<Storey['id'], number> => {
  const storeys = usePanelStoreys();
  return useMemo(() => {
    return storeys.reduce(
      (acc, storey) => {
        acc[storey.id] = getGfaFromStoreys(storey);
        return acc;
      },
      {} as Record<Storey['id'], number>,
    );
  }, [storeys]);
};

export const useMaxStoreyGfa = (): number => {
  const storeyGfaRecord = useStoreyGfaRecord();
  return useMemo(() => {
    const values = Object.values(storeyGfaRecord);
    if (!values.length) return 0;
    return Math.max(...values);
  }, [storeyGfaRecord]);
};

/**
 * Get the minimum storey height from the project geometry
 * @returns The minimum storey height
 */
export const useMinStoreyHeight = (): number => {
  const storeys = useStoreys();
  return useMemo(() => {
    return Math.min(...storeys.map((storey) => storey.height));
  }, [storeys]);
};

/**
 * Check if the top storey (the one that new storeys are cloned from) has shapes
 * @returns true if the top storey has at least one shape, false otherwise
 */
export const useIsAddStoreyAllowed = (): boolean => {
  const storeys = usePanelStoreys();
  return useMemo(() => {
    const topStorey = storeys[storeys.length - 1];
    // If no storeys exist, allow adding the first one
    if (!topStorey) return true;
    // Can add storey if the top storey has at least one shape
    return topStorey.shapes.length > 0;
  }, [storeys]);
};

/**
 * Get the parent storey of a mezzanine storey
 * @param mezzanineStorey
 * @returns The parent storey of the mezzanine storey
 */
export const useMezzanineParentStorey = (
  mezzanineStorey: Storey,
): NonMezzanineStorey | undefined => {
  const storeys = usePanelStoreys();
  return getMezzanineParent(storeys, mezzanineStorey);
};

/**
 * Get the mezzanine storeys in a storey
 * @param storey
 * @returns
 */
export const useMezzanineStoreysInStorey = (
  storey: Storey,
): MezzanineStorey[] => {
  const storeys = usePanelStoreys();

  return useMemo(
    () => getMezzanineStoreysInStorey(storeys, storey),
    [storeys, storey],
  );
};
